import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Spinner } from 'react-bootstrap';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllRead,
  markOneRead,
} from '../../features/notifications/notificationsSlice';
import { getNotificationTargetPath } from '../../services/notificationService';
import { formatDateTimeUTC } from '../../utils/dateFormat';

const UNREAD_POLL_MS = 60000;

const typeIcon = (type) => {
  switch (String(type || '').toLowerCase()) {
    case 'signature_request':
    case 'signature_reminder':
      return 'ri-pen-nib-line';
    case 'signature_email_failed':
      return 'ri-mail-close-line';
    case 'envelope_signed':
      return 'ri-checkbox-circle-line';
    case 'envelope_completed':
      return 'ri-file-check-line';
    case 'envelope_declined':
      return 'ri-close-circle-line';
    case 'envelope_revoked':
      return 'ri-indeterminate-circle-line';
    case 'envelope_expired':
      return 'ri-time-line';
    case 'welcome':
      return 'ri-hand-heart-line';
    case 'password_changed':
      return 'ri-lock-password-line';
    case 'invitation_accepted':
      return 'ri-user-follow-line';
    case 'invitation_expired':
      return 'ri-user-unfollow-line';
    case 'user_suspended':
      return 'ri-user-forbid-line';
    case 'user_reactivated':
      return 'ri-user-add-line';
    case 'user_removed':
      return 'ri-user-minus-line';
    case 'role_changed':
      return 'ri-shield-user-line';
    case 'quota_nearly_used':
      return 'ri-error-warning-line';
    case 'quota_exhausted':
      return 'ri-alarm-warning-line';
    case 'credit_pack_purchased':
      return 'ri-coupon-3-line';
    case 'pay_per_send_succeeded':
      return 'ri-send-plane-line';
    case 'subscription_plan_changed':
      return 'ri-vip-crown-line';
    case 'subscription_renewed':
      return 'ri-refresh-line';
    case 'payment_failed':
      return 'ri-bank-card-line';
    case 'subscription_canceled':
      return 'ri-calendar-close-line';
    case 'apple_refund_revoke':
      return 'ri-apple-line';
    case 'trial_ending':
      return 'ri-hourglass-line';
    default:
      return 'ri-notification-3-line';
  }
};

/** Must forward ref so react-bootstrap can attach outside-click close. */
const NotificationsToggle = forwardRef(
  ({ onClick, unreadCount, badgeLabel, ...rest }, ref) => (
    <button
      {...rest}
      ref={ref}
      type="button"
      className="icon-btn notifications-dropdown__toggle"
      title="Notifications"
      aria-label={
        unreadCount > 0
          ? `Notifications, ${unreadCount} unread`
          : 'Notifications'
      }
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e);
      }}
    >
      <i className="ri-notification-3-line" aria-hidden />
      {unreadCount > 0 && (
        <span className="notifications-dropdown__badge">{badgeLabel}</span>
      )}
    </button>
  )
);
NotificationsToggle.displayName = 'NotificationsToggle';

const NotificationsDropdown = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth?.token);
  const { items, unreadCount, listStatus, listError } = useSelector(
    (state) => state.notifications
  );
  const [open, setOpen] = useState(false);

  const refreshUnread = useCallback(() => {
    if (!token && !localStorage.getItem('auth_token')) return;
    dispatch(fetchUnreadCount());
  }, [dispatch, token]);

  useEffect(() => {
    refreshUnread();
    const timer = setInterval(refreshUnread, UNREAD_POLL_MS);
    const onFocus = () => refreshUnread();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshUnread]);

  const handleToggle = (nextOpen) => {
    setOpen(Boolean(nextOpen));
    if (nextOpen) {
      dispatch(fetchNotifications({ per_page: 20 }));
    }
  };

  const closeMenu = () => setOpen(false);

  const handleMarkAll = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (unreadCount <= 0) return;
    await dispatch(markAllRead());
  };

  const handleItemClick = async (notification) => {
    if (notification.isUnread) {
      dispatch(markOneRead(notification.id));
    }
    const path = getNotificationTargetPath(notification);
    closeMenu();
    if (path) {
      navigate(path);
    }
  };

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <Dropdown
      align="end"
      show={open}
      onToggle={handleToggle}
      className="notifications-dropdown"
    >
      <Dropdown.Toggle
        as={NotificationsToggle}
        id="notifications-dropdown"
        unreadCount={unreadCount}
        badgeLabel={badgeLabel}
      />

      <Dropdown.Menu className="notifications-dropdown__menu shadow" renderOnMount>
        <div className="notifications-dropdown__header d-flex align-items-center justify-content-between px-3 py-2">
          <strong>Notifications</strong>
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            disabled={unreadCount <= 0 || listStatus === 'loading'}
            onClick={handleMarkAll}
          >
            Mark all read
          </button>
        </div>
        <Dropdown.Divider className="my-0" />

        <div className="notifications-dropdown__list">
          {listStatus === 'loading' && items.length === 0 && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          )}

          {listError && (
            <div className="px-3 py-3 text-danger small">{listError}</div>
          )}

          {listStatus !== 'loading' && !listError && items.length === 0 && (
            <div className="px-3 py-4 text-muted text-center small">
              No notifications yet.
            </div>
          )}

          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={[
                'notifications-dropdown__item',
                item.isUnread ? 'is-unread' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleItemClick(item)}
            >
              <span className="notifications-dropdown__icon" aria-hidden>
                <i className={typeIcon(item.type)} />
              </span>
              <span className="notifications-dropdown__content">
                <span className="notifications-dropdown__title">
                  {item.title || 'Notification'}
                </span>
                {item.body && (
                  <span className="notifications-dropdown__body">{item.body}</span>
                )}
                {item.createdAt && (
                  <span className="notifications-dropdown__time text-muted">
                    {formatDateTimeUTC(item.createdAt)}
                  </span>
                )}
              </span>
              {item.isUnread && (
                <span className="notifications-dropdown__dot" aria-hidden />
              )}
            </button>
          ))}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationsDropdown;
